import type { SiteChange } from './shared';
import type { TemplateAdded } from './templateWords';

/**
 * The working copy bar's "What changed" (design review round 27 §4, backlog
 * eec8beb5): the full record of what this publish changes, in groups a person
 * recognises — Look, Menu, Pages and sections, Footer, then anything else —
 * each row `before → after`. Before you publish holds only what to act on;
 * this is everything, so a menu link that goes shows here even when there is
 * nothing to fix. Never "Collection": a section.
 */

export interface ChangeRow {
  key: string;
  label: string;
  before: string | null;
  after: string;
  /** Colour swatches either side (the Colours row). */
  swatchesBefore?: string[];
  swatchesAfter?: string[];
  /** + added (green), − removed (red). */
  mark?: '+' | '−';
  note?: string;
}

export interface ChangeGroup {
  key: string;
  title: string;
  rows: ChangeRow[];
}

const HEX = /^#[0-9a-f]{6}$/i;
const LOOK = /^(starter|preset|colors|fonts|type|borders|customCss)\b/;
const MENU = /^navigation\.(header|cta|phone)\b/;
const FOOTER = /^navigation\.(footer|footerColumns|footerText|social)\b/;

/** "News (/news)" → "News": the menu and section rows name things, not addresses. */
const named = (v: string | null) => String(v || '').replace(/\s*\([^)]*\)\s*$/, '');

const plain = (c: SiteChange): ChangeRow => ({
  key: c.path,
  label: c.label,
  before: c.before,
  after: c.after,
});

export function whatChanged(
  changes: SiteChange[],
  {
    added = null,
    templateNames = {},
  }: { added?: TemplateAdded | null; templateNames?: Record<string, string> } = {}
): ChangeGroup[] {
  const look: ChangeRow[] = [];
  const menu: ChangeRow[] = [];
  const pages: ChangeRow[] = [];
  const footer: ChangeRow[] = [];
  const other: ChangeRow[] = [];

  const colours = changes.filter((c) => c.path.startsWith('colors.'));
  const fonts = changes.filter((c) => c.path.startsWith('fonts.'));
  const menuAdded: string[] = [];
  const menuRemoved: string[] = [];
  const sectionsAdded: string[] = [];
  const sectionsRemoved: string[] = [];

  for (const c of changes) {
    const p = c.path;
    if (p === 'starter') {
      const name = (v: string | null) =>
        v == null || v === 'none' ? 'Your own look' : templateNames[v] || v;
      look.push({ ...plain(c), before: name(c.before), after: name(c.after) });
    } else if (p.startsWith('colors.')) {
      if (c === colours[0]) {
        look.push({
          key: 'colors',
          label: 'Colours',
          before: colours.length > 1 ? 'Current' : c.label,
          after: colours.length > 1 ? 'New palette' : '',
          swatchesBefore: colours.map((x) => x.before || '').filter((v) => HEX.test(v)),
          swatchesAfter: colours.map((x) => x.after).filter((v) => HEX.test(v)),
        });
      }
    } else if (p.startsWith('fonts.')) {
      if (c === fonts[0]) {
        look.push({
          key: 'fonts',
          label: 'Fonts',
          before: fonts.map((x) => x.before || 'none').join(' / '),
          after: fonts.map((x) => x.after).join(' / '),
          note: fonts
            .map((x) => (x.path === 'fonts.heading' ? 'Headings' : 'Body text'))
            .join(' / '),
        });
      }
    } else if (LOOK.test(p) || (p.startsWith('layout.') && p !== 'layout.homePage')) {
      look.push(plain(c));
    } else if (p === 'layout.homePage') {
      pages.push({ ...plain(c), label: 'Front page' });
    } else if (MENU.test(p)) {
      if (/^navigation\.header\[[^\]]*\]$/.test(p) && c.label.endsWith('added'))
        menuAdded.push(named(c.after));
      else if (/^navigation\.header\[[^\]]*\]$/.test(p) && c.label.endsWith('removed'))
        menuRemoved.push(named(c.before));
      else menu.push(plain(c));
    } else if (FOOTER.test(p)) {
      footer.push({
        ...plain(c),
        label: c.label.replace(/^Footer /, '').replace(/^\w/, (x) => x.toUpperCase()),
      });
    } else if (/^collections\[[^\]]*\]$/.test(p) && c.label === 'Section added') {
      sectionsAdded.push(named(c.after));
    } else if (/^collections\[[^\]]*\]$/.test(p) && c.label === 'Section removed') {
      sectionsRemoved.push(named(c.before));
    } else if (p.startsWith('collections')) {
      pages.push(plain(c));
    } else {
      other.push(plain(c));
    }
  }

  const list = (xs: string[]) => xs.join(', ');
  if (menuAdded.length)
    menu.unshift({ key: 'menu+', label: 'Added', before: null, after: list(menuAdded), mark: '+' });
  if (menuRemoved.length) {
    menu.splice(menuAdded.length ? 1 : 0, 0, {
      key: 'menu-',
      label: 'Removed',
      before: null,
      after: list(menuRemoved),
      mark: '−',
      note: 'Only the menu links go. The pages, sections and articles they lead to stay.',
    });
  }

  const draftPages = (added?.pages || []).map((x) => x.title || 'Untitled page');
  const draftForms = (added?.forms || []).map((x) => x.name || 'Untitled form');
  const front = pages.filter((r) => r.key === 'layout.homePage');
  const rest = pages.filter((r) => r.key !== 'layout.homePage');
  const pageRows: ChangeRow[] = [
    ...(draftPages.length
      ? [
          {
            key: 'pages+',
            label: 'Pages',
            before: null,
            after: list(draftPages),
            mark: '+' as const,
            note: 'Drafts until you publish the theme.',
          },
        ]
      : []),
    ...front,
    ...(sectionsAdded.length
      ? [
          {
            key: 'sections+',
            label: 'Sections',
            before: null,
            after: list(sectionsAdded),
            mark: '+' as const,
            note: 'Empty until articles are filed in them.',
          },
        ]
      : []),
    ...(sectionsRemoved.length
      ? [
          {
            key: 'sections-',
            label: 'Sections',
            before: null,
            after: list(sectionsRemoved),
            mark: '−' as const,
            note: 'Their pages stop working. The articles stay.',
          },
        ]
      : []),
    ...rest,
    ...(draftForms.length
      ? [
          {
            key: 'forms+',
            label: 'Form',
            before: null,
            after: list(draftForms),
            mark: '+' as const,
          },
        ]
      : []),
  ];

  return [
    { key: 'look', title: 'Look', rows: look },
    { key: 'menu', title: 'Menu', rows: menu },
    { key: 'pages', title: 'Pages and sections', rows: pageRows },
    { key: 'footer', title: 'Footer', rows: footer },
    { key: 'other', title: 'Everything else', rows: other },
  ].filter((g) => g.rows.length);
}

export const rowCount = (groups: ChangeGroup[]) => groups.reduce((n, g) => n + g.rows.length, 0);
